import type { ClientConsentKind, ClientConsentView } from "@planerius/contracts/cabinet"

/**
 * ВКЛАДКА «СОГЛАСИЯ» (ТЗ 03, 3-А5; макет «Каталог и клиенты - П2») — правила без вёрстки.
 *
 * Главное требование ТЗ: человек за стойкой не может подумать, что «галочка стоит», когда
 * рекламного согласия нет. Отсюда три состояния словами и значком, а не цветом:
 * «● Дано», «✕ Отозвано», «○ Не запрашивалось» — последнее НЕ отказ, и оно так и подписано.
 *
 * СОСТОЯНИЕ ВИДА СЧИТАЕТСЯ ТАК ЖЕ, КАК У ОТБОРА ПОЛУЧАТЕЛЕЙ: есть хоть одна неотозванная
 * строка — дано. Экран, считающий иначе, показал бы «отозвано», пока рассылка уходит.
 */

export type ConsentState = "given" | "revoked" | "never"

export const STATE_LABEL: Readonly<Record<ConsentState, string>> = {
  given: "● Дано",
  revoked: "✕ Отозвано",
  never: "○ Не запрашивалось",
}

export const CONSENT_TITLE: Readonly<Record<ClientConsentKind, string>> = {
  pd: "Обработка персональных данных",
  ads: "Рекламные и информационные рассылки",
  photo: "Публикация фото работ",
  channel_sms: "Канал: SMS",
  channel_telegram: "Канал: Telegram",
  channel_max: "Канал: MAX",
  channel_push: "Канал: push в приложении",
  channel_email: "Канал: e-mail",
}

/** Виды под двумя главными блоками — сеткой, в порядке макета. */
export const OTHER_KINDS: readonly ClientConsentKind[] = [
  "photo",
  "channel_telegram",
  "channel_sms",
  "channel_max",
  "channel_push",
  "channel_email",
]

const SOURCE_LABEL: Readonly<Record<string, string>> = {
  web: "витрина",
  telegram: "Telegram-бот",
  max: "бот MAX",
  mobile: "приложение",
  widget: "виджет на сайте",
  admin: "администратор",
  api: "по API",
}

export interface ConsentLine {
  readonly kind: ClientConsentKind
  readonly state: ConsentState
  /** Строка, по которой показано состояние: живая или последняя отозванная. */
  readonly current: ClientConsentView | null
  /** Все строки вида, новые сверху, — для раскрытия «история». */
  readonly history: readonly ClientConsentView[]
}

export function lineOf(consents: readonly ClientConsentView[], kind: ClientConsentKind): ConsentLine {
  const history = consents
    .filter((item) => item.kind === kind)
    .toSorted((a, b) => b.grantedAt.localeCompare(a.grantedAt))
  const live = history.find((item) => item.revokedAt === null)
  if (live !== undefined) return { kind, state: "given", current: live, history }
  const revoked = history.toSorted((a, b) => (b.revokedAt ?? "").localeCompare(a.revokedAt ?? ""))[0]
  if (revoked !== undefined) return { kind, state: "revoked", current: revoked, history }
  return { kind, state: "never", current: null, history }
}

/** «12 марта 2026» — без дня недели: дата доказательства, а не планов. */
export function dateOf(instant: string, timeZone: string): string {
  const parsed = new Date(instant)
  if (Number.isNaN(parsed.getTime())) return "—"
  return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", year: "numeric", timeZone })
    .format(parsed)
    .replace(/\s?г\.$/, "")
}

/** «Редакция от 1 марта 2026» для версий-дат; иначе — как записано. */
export function versionLabel(version: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(version)) {
    return `Редакция от ${dateOf(`${version}T12:00:00Z`, "UTC")}`
  }
  return `Редакция «${version}»`
}

/** Кто и где дал согласие: «витрина · iPhone», «администратор Ирина В.». */
export function grantedVia(consent: ClientConsentView): string {
  if (consent.source === "admin" && consent.grantedByName !== null) {
    return `администратор ${consent.grantedByName}`
  }
  const where = consent.source === null ? "источник не записан" : (SOURCE_LABEL[consent.source] ?? consent.source)
  return consent.device === null ? where : `${where} · ${consent.device}`
}

/** Как отозвано — это разные основания, и экран их не смешивает. */
export function revokedVia(consent: ClientConsentView): string {
  switch (consent.revokeSource) {
    case "staff":
      return consent.revokedByName === null
        ? "в кабинете по требованию клиента"
        : `по требованию клиента, отметил(а) ${consent.revokedByName}`
    case "client":
      return "клиент сам — по ссылке отписки или в чате"
    case "merge":
      return "при объединении карточек: в другой было отозвано"
    case "erasure":
      return "при удалении данных клиента"
    case null:
      return "как отозвано, не записано"
  }
}

/** Подпись под состоянием — у главных блоков. */
export function subOf(line: ConsentLine, timeZone: string): string {
  const consent = line.current
  if (consent === null) {
    return line.kind === "pd"
      ? "Отметится при следующей записи — без него запись не сохранится"
      : "Спросите при визите — это не отказ"
  }
  if (line.state === "revoked" && consent.revokedAt !== null) {
    return `${dateOf(consent.revokedAt, timeZone)} · ${revokedVia(consent)} · было дано ${dateOf(consent.grantedAt, timeZone)}`
  }
  return `${dateOf(consent.grantedAt, timeZone)} · ${grantedVia(consent)}`
}

/** Подпись в сетке прочих согласий — короче, но с тем же смыслом. */
export function otherSubOf(line: ConsentLine, timeZone: string): string {
  const consent = line.current
  if (consent === null) return line.kind === "photo" ? "Портфолио и витрина" : "Не спрашивали"
  if (line.state === "revoked" && consent.revokedAt !== null) {
    return `${dateOf(consent.revokedAt, timeZone)} · ${revokedVia(consent)}`
  }
  return `${dateOf(consent.grantedAt, timeZone)} · ${grantedVia(consent)}`
}

/** Что можно делать с рекламой — строка под блоком, ровно о последствиях. */
export function adsHint(state: ConsentState): string {
  switch (state) {
    case "given":
      return "Акции и поздравления отправлять можно"
    case "revoked":
      return "Повторно не спрашиваем без повода"
    case "never":
      return "Надо спросить. Без отдельного согласия — только напоминания о его записях"
  }
}

/**
 * Свежий отзыв рекламы самим клиентом (ТЗ 3-А5, «Конфликт»): сообщение обязано быть
 * заметным — это ограничение, а не обновление данных. «Свежий» — за последние сутки:
 * дальше это просто состояние, и плашка в красном превратилась бы в обои.
 */
export function freshClientRevocation(line: ConsentLine, now: Date): boolean {
  const consent = line.current
  if (line.state !== "revoked" || consent === null || consent.revokedAt === null) return false
  if (consent.revokeSource !== "client") return false
  return now.getTime() - new Date(consent.revokedAt).getTime() < 24 * 3600 * 1000
}

/** Виды, которые сейчас действуют, — только их можно отозвать. */
export function revocableKinds(consents: readonly ClientConsentView[]): ClientConsentKind[] {
  const kinds: ClientConsentKind[] = []
  for (const kind of ["pd", "ads", ...OTHER_KINDS] as const) {
    if (lineOf(consents, kind).state === "given") kinds.push(kind)
  }
  return kinds
}

/**
 * Дата бумаги из поля даты → момент для сервера. Сегодня — не передаём вовсе: сервер
 * поставит «сейчас», и отметка не окажется «в будущем» из-за полудня по Гринвичу.
 */
export function grantedAtOf(date: string, today: string): string | undefined {
  if (date === "" || date >= today) return undefined
  return `${date}T12:00:00.000Z`
}

/** Проверка формы бумажного согласия — до сервера, теми же словами, что в ТЗ. */
export function paperProblem(input: { readonly version: string; readonly date: string; readonly today: string }): string | null {
  if (input.version.trim() === "") {
    return "Укажите, какую редакцию согласия подписал клиент, — без неё отметка не будет иметь силы при проверке"
  }
  if (input.date !== "" && input.date > input.today) {
    return "Бумагу можно подписать вчера, но не завтра. Проверьте дату"
  }
  return null
}
